/**
 * GraphQL interface editor (ADR-0058, the SDL-derived twin of REST) — the app's
 * `kind="graphql"` interface exposes a curated list of RESOURCES (table / dataset
 * / query) over ONE schema-wide `run_as_role` boundary + DoS caps. There is a
 * SINGLE endpoint `POST /api/apps/<slug>/graphql`; the SDL is derived server-side
 * from these resources (never hand-authored). Twin of `RestInterfaceEditor`.
 *
 * MASTER/DETAIL (WS-8, like `RestInterfaceEditor`): a `view` prop drives whether
 * we render the resource LIST (in the builder's lateral sidebar) or the selected
 * item's DETAIL (full-width main pane), selection lifted to the caller. The list
 * has a pinned **Schema** row (selection sentinel `__schema__`) for the schema-wide
 * role + caps — the GraphQL analogue of REST's per-endpoint role, but ONE for the
 * whole graph (a single traversal can't switch boundary).
 *
 * Admin console section — native `<select>` is acceptable (repo convention).
 * i18n: inline `t(key, fallback)` with Italian fallbacks.
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Boxes, Database, FileCode, Network, Plus, Settings2, Table2, Trash2 } from "lucide-react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { appsApi } from "@/lib/api-apps";
import {
  emptyResource,
  MAX_DEPTH_DEFAULT,
  MAX_NODES_DEFAULT,
  parseGraphqlConfig,
  type GraphqlConfig,
  type GraphqlResource,
  type GraphqlSource,
} from "@/lib/graphqlResources";

const SELECT = "h-9 w-full rounded-md border border-border bg-surface px-2 text-sm";
export type GraphqlView = "list" | "detail" | "full";
/** The pinned first-row selection = the schema-wide settings (role + caps). */
const SCHEMA_SEL = "__schema__";

const SOURCE_ICON: Record<GraphqlSource, typeof Table2> = {
  table: Table2,
  dataset: Database,
  query: FileCode,
};

type Opt = { id: string; label: string };

function useGraphql(appId: string) {
  const qc = useQueryClient();
  const ifacesQ = useQuery({ queryKey: ["apps", appId, "interfaces"], queryFn: () => appsApi.listInterfaces(appId) });
  const iface = (ifacesQ.data ?? []).find((i) => i.kind === "graphql") ?? null;
  const config = React.useMemo(() => parseGraphqlConfig(iface?.config), [iface?.config]);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["apps", appId, "interfaces"] });
  const create = useMutation({
    mutationFn: () =>
      appsApi.createInterface(appId, {
        kind: "graphql",
        name: "GraphQL API",
        config: { resources: [], max_depth: MAX_DEPTH_DEFAULT, max_nodes: MAX_NODES_DEFAULT },
        standalone: true,
        embedded: false,
      }),
    onSuccess: invalidate,
  });
  const write = useMutation({
    mutationFn: (cfg: GraphqlConfig) => appsApi.updateInterface(appId, iface!.id, { config: cfg as unknown as Record<string, unknown> }),
    onSuccess: invalidate,
  });
  // Presence toggle (assistant-interface pattern): flip the whole interface on/off.
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => appsApi.updateInterface(appId, iface!.id, { enabled }),
    onSuccess: invalidate,
  });
  const del = useMutation({
    mutationFn: () => appsApi.deleteInterface(appId, iface!.id),
    onSuccess: invalidate,
  });
  return { loading: ifacesQ.isLoading, iface, config, create, write, toggle, del };
}

function useOptions(appId: string) {
  const tablesQ = useQuery({ queryKey: ["apps", appId, "tables"], queryFn: () => appsApi.listTables(appId) });
  const datasetsQ = useQuery({ queryKey: ["apps", appId, "datasets"], queryFn: () => appsApi.listDatasets(appId) });
  const queriesQ = useQuery({ queryKey: ["apps", appId, "queries"], queryFn: () => appsApi.listQueries(appId) });
  const rolesQ = useQuery({ queryKey: ["apps", appId, "roles"], queryFn: () => appsApi.listRoles(appId) });
  return {
    tables: (tablesQ.data ?? []).map((x) => ({ id: x.id, label: x.name })),
    datasets: (datasetsQ.data ?? []).map((x) => ({ id: x.id, label: x.name })),
    queries: (queriesQ.data ?? []).map((x) => ({ id: x.id, label: `${x.name} (${x.key})` })),
    roles: (rolesQ.data ?? []).map((x) => ({ id: x.id, label: x.name })),
  };
}

export function GraphqlInterfaceEditor({
  appId,
  canWrite,
  view = "full",
  selectedId,
  onSelectId,
}: {
  appId: string;
  canWrite: boolean;
  view?: GraphqlView;
  selectedId?: string | null;
  onSelectId?: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const gql = useGraphql(appId);
  const opts = useOptions(appId);
  const [innerSel, setInnerSel] = React.useState<string | null>(SCHEMA_SEL);
  const sel = view === "full" ? innerSel : selectedId ?? null;
  const setSel = view === "full" ? setInnerSel : onSelectId ?? (() => {});

  if (gql.loading) return <div className="p-4 text-sm text-muted-foreground">…</div>;

  const cfg = gql.config;
  const exists = !!gql.iface;
  const enabled = gql.iface?.enabled ?? false;
  const addResource = () => {
    if (!gql.iface) return;
    const r = emptyResource("table");
    gql.write.mutate({ ...cfg, resources: [...cfg.resources, r] }, { onSuccess: () => setSel(r.id) });
  };

  // The Abilitato toggle governs PRESENCE (assistant-interface pattern, ADR-0049):
  // with no interface yet, turning it ON creates it; otherwise it flips `enabled`.
  const setPresence = (on: boolean) => {
    if (!gql.iface) {
      if (on) gql.create.mutate();
      return;
    }
    gql.toggle.mutate(on);
  };
  const removeInterface = () => {
    if (!gql.iface) return;
    if (window.confirm(t("apps.gqlDeleteConfirm", "Eliminare l'interfaccia GraphQL e tutte le sue risorse?")))
      gql.del.mutate(undefined, { onSuccess: () => setSel(SCHEMA_SEL) });
  };
  const busy = gql.create.isPending || gql.toggle.isPending || gql.del.isPending;

  const list = (
    <GraphqlList
      exists={exists}
      enabled={enabled}
      busy={busy}
      cfg={cfg}
      opts={opts}
      canWrite={canWrite}
      selectedId={sel}
      onSelect={setSel}
      onCreate={addResource}
      onTogglePresence={setPresence}
      onDelete={removeInterface}
    />
  );

  const detail = !exists ? (
    <GraphqlInactive
      title={t("apps.gqlInterface", "API GraphQL")}
      hint={t(
        "apps.gqlInactiveHint",
        "Esponi tabelle, proiezioni e Query su un unico endpoint GraphQL. Attiva l'interruttore «Abilitato» a sinistra per crearla, poi aggiungi le risorse.",
      )}
    />
  ) : sel === SCHEMA_SEL ? (
    <SchemaDetail cfg={cfg} opts={opts} canWrite={canWrite} saving={gql.write.isPending} onSave={(next) => gql.write.mutate(next)} />
  ) : (
    <ResourceDetail
      key={sel ?? "none"}
      resource={sel ? cfg.resources.find((r) => r.id === sel) ?? null : null}
      opts={opts}
      canWrite={canWrite}
      saving={gql.write.isPending}
      onCreate={addResource}
      onSave={(draft) => gql.write.mutate({ ...cfg, resources: cfg.resources.map((r) => (r.id === draft.id ? draft : r)) })}
      onDelete={(id) => gql.write.mutate({ ...cfg, resources: cfg.resources.filter((r) => r.id !== id) }, { onSuccess: () => setSel(SCHEMA_SEL) })}
    />
  );

  // The interface config is validated server-side on every write; a bad resource
  // (unknown name, non-table writable) 422s the whole save. Surface it.
  const writeErr = gql.write.error ? (
    <Alert tone="danger" title={t("apps.gqlSaveFailed", "Salvataggio non riuscito")}>
      {String((gql.write.error as Error)?.message ?? gql.write.error)}
    </Alert>
  ) : null;

  if (view === "list")
    return (
      <>
        {writeErr}
        {list}
      </>
    );
  if (view === "detail")
    return (
      <>
        {writeErr}
        {detail}
      </>
    );
  return (
    <div className="space-y-3">
      {writeErr}
      <div className="grid min-h-[520px] grid-cols-[16rem_1fr] gap-4">
        <div className="rounded-lg border border-border p-2">{list}</div>
        <div>{detail}</div>
      </div>
    </div>
  );
}

/** The resource MASTER list — presence header + Elimina, a pinned Schema row, and
 *  one row per exposed resource. */
function GraphqlList({
  exists,
  enabled,
  busy,
  cfg,
  opts,
  canWrite,
  selectedId,
  onSelect,
  onCreate,
  onTogglePresence,
  onDelete,
}: {
  exists: boolean;
  enabled: boolean;
  busy: boolean;
  cfg: GraphqlConfig;
  opts: ReturnType<typeof useOptions>;
  canWrite: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onCreate: () => void;
  onTogglePresence: (on: boolean) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const labelFor = (r: GraphqlResource): string => {
    if (r.name) return r.name;
    const pool = r.source === "table" ? opts.tables : r.source === "dataset" ? opts.datasets : opts.queries;
    return pool.find((o) => o.id === r.target_id)?.label ?? t("apps.gqlDraft", "(bozza)");
  };
  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Interface header — title + Elimina, then the Abilitato toggle that GOVERNS
          PRESENCE (assistant-interface pattern): no separate "create" step. */}
      <div className="mb-2 shrink-0 space-y-2">
        <div className="flex items-center justify-between gap-2 px-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("apps.gqlInterface", "API GraphQL")}
          </span>
          {exists && canWrite && (
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              title={t("common.delete", "Elimina")}
              className="rounded p-0.5 text-danger hover:bg-danger/10 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          )}
        </div>
        <label className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface/40 px-2 py-1.5">
          <span className="flex min-w-0 flex-col">
            <span className="text-[12px] font-medium">{t("common.enabled", "Abilitato")}</span>
            <span className="text-[10px] text-muted-foreground">
              {exists
                ? t("apps.gqlEnabledHint", "Disattiva per nascondere l'API mantenendo la configurazione.")
                : t("apps.gqlEnableToCreate", "Attiva per creare l'interfaccia GraphQL.")}
            </span>
          </span>
          <Switch checked={exists && enabled} disabled={!canWrite || busy} onCheckedChange={onTogglePresence} />
        </label>
      </div>

      {!exists ? (
        <p className="px-1 py-2 text-[11px] text-muted-foreground">
          {t("apps.gqlNotCreated", "Nessuna interfaccia GraphQL. Attiva l'interruttore qui sopra per crearla.")}
        </p>
      ) : (
        <>
          {/* Pinned Schema row — the schema-wide role + caps. */}
          <button
            type="button"
            onClick={() => onSelect(SCHEMA_SEL)}
            className={`mb-1 flex shrink-0 items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] ${selectedId === SCHEMA_SEL ? "bg-accent/15 text-accent" : "hover:bg-surface"}`}
          >
            <Settings2 className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
            <span className="font-medium">{t("apps.gqlSchema", "Schema (ruolo + limiti)")}</span>
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={onCreate}
              className="mb-1 flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("apps.gqlAddResource", "Nuova risorsa")}
            </button>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {cfg.resources.length === 0 ? (
              <p className="px-1 py-2 text-[11px] text-muted-foreground">
                {t("apps.gqlEmptyList", "Nessuna risorsa. Aggiungine una con «Nuova risorsa».")}
              </p>
            ) : (
              cfg.resources.map((r) => {
                const Icon = SOURCE_ICON[r.source] ?? Boxes;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onSelect(r.id)}
                    className={`mb-0.5 flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left ${r.id === selectedId ? "bg-accent/15 text-accent" : "hover:bg-surface"}`}
                  >
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${r.enabled ? "bg-success" : "bg-muted-foreground/40"}`} />
                    <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                    <span className="min-w-0 flex-1 truncate font-mono text-[11px]">{labelFor(r)}</span>
                    {r.writable && <span className="shrink-0 rounded bg-warning/15 px-1 text-[8px] font-semibold uppercase text-warning">RW</span>}
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** The DETAIL-column placeholder shown when the interface isn't created yet — fills
 *  the pane (no black void) and points to the Abilitato toggle in the list. */
function GraphqlInactive({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-3 px-6 text-center">
      <Network className="h-8 w-8 text-muted-foreground/50" strokeWidth={1.5} />
      <div className="text-sm font-semibold text-muted-foreground">{title}</div>
      <p className="max-w-sm text-xs text-muted-foreground/80">{hint}</p>
    </div>
  );
}

/** The schema-wide settings — ONE `run_as_role` + the DoS caps (depth / nodes). */
function SchemaDetail({
  cfg,
  opts,
  canWrite,
  saving,
  onSave,
}: {
  cfg: GraphqlConfig;
  opts: ReturnType<typeof useOptions>;
  canWrite: boolean;
  saving: boolean;
  onSave: (next: GraphqlConfig) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = React.useState<GraphqlConfig>(cfg);
  React.useEffect(() => setDraft(cfg), [cfg]);
  const patch = (up: Partial<GraphqlConfig>) => setDraft((d) => ({ ...d, ...up }));

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <Settings2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
        <span className="flex-1 text-sm font-semibold">{t("apps.gqlSchema", "Schema (ruolo + limiti)")}</span>
        {canWrite && (
          <Button size="sm" onClick={() => onSave(draft)} disabled={saving}>
            {t("common.save", "Salva")}
          </Button>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pt-4">
        <Section title={t("apps.gqlSecSecurity", "Sicurezza")}>
          <Field
            label={t("apps.gqlRunAs", "Esegui come ruolo")}
            hint={t(
              "apps.gqlRunAsHint",
              "L'intero schema gira sotto questo ruolo — RLS + mascheramento campi si applicano a OGNI resolver, anche annidato. Vuoto = accesso pieno (operatore).",
            )}
          >
            <select className={SELECT} value={draft.run_as_role_id ?? ""} disabled={!canWrite} onChange={(e) => patch({ run_as_role_id: e.target.value || null })}>
              <option value="">{t("apps.gqlRunAsNone", "Operatore (senza restrizioni)")}</option>
              {opts.roles.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </Field>
        </Section>

        <Section title={t("apps.gqlSecLimits", "Limiti anti-abuso")}>
          <p className="text-[11px] text-muted-foreground/70">
            {t("apps.gqlLimitsHint", "GraphQL permette query annidate arbitrarie — questi limiti bloccano una query troppo profonda o troppo grande prima di eseguirla.")}
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label={t("apps.gqlMaxDepth", "Profondità massima")} hint={t("apps.gqlMaxDepthHint", "Livelli di annidamento (max 20).")}>
              <Input
                type="number"
                min={1}
                max={20}
                className="h-9"
                value={draft.max_depth}
                disabled={!canWrite}
                onChange={(e) => patch({ max_depth: clampInt(e.target.value, 1, 20, MAX_DEPTH_DEFAULT) })}
              />
            </Field>
            <Field label={t("apps.gqlMaxNodes", "Nodi massimi")} hint={t("apps.gqlMaxNodesHint", "Campi totali nella query (max 5000).")}>
              <Input
                type="number"
                min={1}
                max={5000}
                className="h-9"
                value={draft.max_nodes}
                disabled={!canWrite}
                onChange={(e) => patch({ max_nodes: clampInt(e.target.value, 1, 5000, MAX_NODES_DEFAULT) })}
              />
            </Field>
          </div>
        </Section>

        <Section title={t("apps.gqlSecEndpoint", "Endpoint")}>
          <p className="text-[11px] text-muted-foreground/70">
            {t("apps.gqlEndpointNote", "Endpoint unico:")}{" "}
            <span className="font-mono">POST /api/apps/{"{slug}"}/graphql</span>. {t("apps.gqlIntrospectNote", "L'introspezione GraphQL è integrata.")}
          </p>
        </Section>
      </div>
    </div>
  );
}

/** A single exposed resource — source + target + name + writable + enabled. */
function ResourceDetail({
  resource,
  opts,
  canWrite,
  saving,
  onCreate,
  onSave,
  onDelete,
}: {
  resource: GraphqlResource | null;
  opts: ReturnType<typeof useOptions>;
  canWrite: boolean;
  saving: boolean;
  onCreate: () => void;
  onSave: (draft: GraphqlResource) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = React.useState<GraphqlResource | null>(resource);
  React.useEffect(() => setDraft(resource), [resource]);

  if (!draft) {
    return (
      <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
        <span>{t("apps.gqlPickResource", "Seleziona una risorsa dalla lista.")}</span>
        {canWrite && (
          <Button variant="outline" onClick={onCreate}>
            <Plus className="mr-1.5 h-4 w-4" strokeWidth={2} /> {t("apps.gqlAddResource", "Nuova risorsa")}
          </Button>
        )}
      </div>
    );
  }

  const patch = (up: Partial<GraphqlResource>) => setDraft((d) => (d ? { ...d, ...up } : d));
  const optionsFor = (s: GraphqlSource): Opt[] => (s === "table" ? opts.tables : s === "dataset" ? opts.datasets : opts.queries);
  // Changing the source resets the target (a table id isn't a dataset id) + drops
  // `writable` (mutations are table-only, validated server-side).
  const changeSource = (source: GraphqlSource) => patch({ source, target_id: null, writable: false });

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        <span className="min-w-0 flex-1 truncate font-mono text-sm font-semibold">
          {draft.name || optionsFor(draft.source).find((o) => o.id === draft.target_id)?.label || t("apps.gqlDraft", "(bozza)")}
        </span>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {t("common.enabled", "Abilitato")}
          <Switch checked={draft.enabled} disabled={!canWrite} onCheckedChange={(v) => patch({ enabled: v })} />
        </label>
        {canWrite && (
          <>
            <Button size="sm" onClick={() => onSave(draft)} disabled={saving}>
              {t("common.save", "Salva")}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(draft.id)} title={t("common.delete", "Elimina")}>
              <Trash2 className="h-4 w-4 text-danger" strokeWidth={1.5} />
            </Button>
          </>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pt-4">
        <Section title={t("apps.gqlSecSource", "Sorgente dati")}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label={t("apps.gqlSource", "Sorgente")}>
              <select className={SELECT} value={draft.source} disabled={!canWrite} onChange={(e) => changeSource(e.target.value as GraphqlSource)}>
                <option value="table">{t("apps.gqlSourceTable", "Tabella")}</option>
                <option value="dataset">{t("apps.gqlSourceDataset", "Proiezione")}</option>
                <option value="query">{t("apps.gqlSourceQuery", "Query")}</option>
              </select>
            </Field>
            <Field label={t("apps.gqlTarget", "Destinazione")} hint={t("apps.gqlTargetHint", "Vuoto = bozza (inerte finché non scegli).")}>
              <select className={SELECT} value={draft.target_id ?? ""} disabled={!canWrite} onChange={(e) => patch({ target_id: e.target.value || null })}>
                <option value="">{t("apps.gqlPickTarget", "— scegli —")}</option>
                {optionsFor(draft.source).map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </Field>
          </div>
        </Section>

        <Section title={t("apps.gqlSecNaming", "Nome nello schema")}>
          <Field
            label={t("apps.gqlName", "Nome del campo GraphQL")}
            hint={t("apps.gqlNameHint", "Vuoto = derivato dal nome della destinazione. Deve essere un identificatore GraphQL valido e unico.")}
          >
            <Input
              className="h-9 font-mono"
              value={draft.name ?? ""}
              disabled={!canWrite}
              placeholder={t("apps.gqlNamePlaceholder", "es. companies")}
              onChange={(e) => patch({ name: e.target.value || null })}
            />
          </Field>
        </Section>

        {/* Mutations are TABLE-only (validated server-side → 422 otherwise). */}
        {draft.source === "table" && (
          <Section title={t("apps.gqlSecMutations", "Mutazioni")}>
            <label className="flex items-start gap-2">
              <Switch checked={!!draft.writable} disabled={!canWrite} onCheckedChange={(v) => patch({ writable: v })} />
              <span className="text-xs text-muted-foreground">
                {t("apps.gqlWritable", "Esponi create / update / delete")}
                <span className="mt-0.5 block text-[11px] text-muted-foreground/70">
                  {t("apps.gqlWritableHint", "La guardia di scrittura del ruolo continua a governare ogni scrittura — abilitarlo la OFFRE soltanto.")}
                </span>
              </span>
            </label>
          </Section>
        )}
      </div>
    </div>
  );
}

/** Parse + clamp a number input, falling back to `def` on a non-number. */
function clampInt(raw: string, lo: number, hi: number, def: number): number {
  const n = parseInt(raw, 10);
  if (Number.isNaN(n)) return def;
  return Math.max(lo, Math.min(hi, n));
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      {children}
    </section>
  );
}

function Field({ label, hint, className, children }: { label: string; hint?: string; className?: string; children: React.ReactNode }) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ""}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-muted-foreground/70">{hint}</span>}
    </label>
  );
}
